import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../features/auth/authSlice';
import SkeletonLoader from '../../components/skeletonLoader';
import ProgressBar from '../../components/progressBar';

const AllCourses = ({ dir }) => {

    const [modules, setModules] = useState([]);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [loadingModules, setLoadingModules] = useState(true);
    const token = localStorage.getItem("token");
    const profileData = JSON.parse(localStorage.getItem("profileData"));
    const departmentId = profileData?.department?.id || null;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch()

    const base_url = process.env.REACT_APP_LMS_APP_BASE_URL;

    const handleSignOut = useCallback(() => {
        localStorage.removeItem("user");
        localStorage.removeItem("token");
        localStorage.removeItem("profileData");
        sessionStorage.removeItem("conversation");
        sessionStorage.removeItem("currentSessionId");
        sessionStorage.removeItem("sessionNumber");
        sessionStorage.removeItem("sessions");
        dispatch(logout())
        navigate("/login");
    }, [navigate, dispatch]);

    const fetchModules = useCallback(async () => {
        const moduleTypes = [
            { type: 'onboarding', param: 'onboarding' },
            { type: 'onjobtraining', param: 'on-job-training' },
        ];
        try {
            const requests = moduleTypes.map((item) =>
                axios.get(
                    `${base_url}/admin/${item.type}/modules/get-department-modules/${departmentId}`,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                        params: { type: item.param }, // ✅ FIX HERE
                    }
                )
            );
            const responses = await Promise.all(requests);
            const allModules = responses.flatMap((res) => res.data?.data?.department?.modules || []);
            setModules(allModules);
        } catch (error) {
            if (error?.response?.status === 401) {
                handleSignOut();
            }
            console.error('Error fetching department modules:', error);
        } finally {
            setLoadingModules(false);
        }
    }, [token, departmentId, base_url, handleSignOut]);

    useEffect(() => {
        fetchModules();
    }, [token, departmentId, fetchModules]);

    // Get sidebar state and handle responsive layout
    useEffect(() => {
        const updateSidebarState = () => {
            const sidebarState = getComputedStyle(document.documentElement).getPropertyValue('--sidebar-open');
            setSidebarOpen(sidebarState === '1');
        };

        updateSidebarState();

        const handleResize = () => {
            const mobile = window.innerWidth < 1024;
            setIsMobile(mobile);
        };

        handleResize();
        window.addEventListener('resize', handleResize);

        const observer = new MutationObserver(updateSidebarState);
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const getContentPadding = () => {
        if (isMobile) {
            return "";
        }
        return sidebarOpen
            ? (dir === 'rtl' ? 'mr-64' : 'ml-64')
            : (dir === 'rtl' ? 'mr-20' : 'ml-20');
    };

    function showBgColor(status) {
        if (status === 'completed') {
            return "bg-gradient-to-br from-[#A73884] to-[#803A85]";
        } else if (status === 'not_started') {
            return "bg-[linear-gradient(90deg,rgba(131,58,180,1)_0%,rgba(252,176,69,1)_0%,rgba(253,29,29,1)_100%)]";
        } else if (status === 'in_progress') {
            return "bg-[linear-gradient(90deg,rgba(237,221,83,1)_0%,rgba(87,199,133,1)_0%,rgba(69,168,142,1)_66%,rgba(42,123,155,1)_100%)]";
        } else {
            return "";
        }
    }

    let rtlIconPosition = dir === "rtl" ? 'justify-end' : 'justify-start'
    let rtlTextPosition = dir === "rtl" ? 'text-end' : 'text-start'
    let rtlFlexTextPosition = dir === "rtl" ? 'flex-row-reverse' : 'flex-row'

    return (
        <main className={`transition-all duration-300 ${getContentPadding()} p-4`}>
            <header className="bg-white/80 backdrop-blur-xl border-b border-gray-200/50 pb-6 z-10">
                <div className={`flex items-center ${rtlIconPosition}`}>
                    <div className={`flex items-center gap-4 ${rtlIconPosition}`}>
                        <button onClick={() => navigate("/lms/courses")} style={{ backgroundColor: "#767676" }} className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center transition-all duration-300 cursor-pointer">
                            <i className="ri-arrow-left-line text-xl text-white" />
                        </button>
                        <div>
                            <h2 className={`text-xl font-medium text-gray-900 ${rtlTextPosition}`}>
                                {t('All Courses')}
                            </h2>
                            <p className={`text-gray-600 ${rtlTextPosition}`}>{t('Browse and explore all available courses')}</p>
                        </div>
                    </div>
                </div>
            </header>
            {loadingModules ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
                    <SkeletonLoader count={1} className="w-full" height={'300px'} style={{ borderRadius: '1rem' }} />
                    <SkeletonLoader count={1} className="w-full" height={'300px'} style={{ borderRadius: '1rem' }} />
                    <SkeletonLoader count={1} className="w-full" height={'300px'} style={{ borderRadius: '1rem' }} />
                    <SkeletonLoader count={1} className="w-full" height={'300px'} style={{ borderRadius: '1rem' }} />
                    <SkeletonLoader count={1} className="w-full" height={'300px'} style={{ borderRadius: '1rem' }} />
                    <SkeletonLoader count={1} className="w-full" height={'300px'} style={{ borderRadius: '1rem' }} />
                </div>
            ) : modules.length === 0 ? (
                <div className="text-center text-gray-500">No modules found.</div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
                    <>
                        {modules?.slice().reverse().map((item, index) => {
                            const createdAt = item?.module?.users[0]?.moduleProgress[0]?.createdAt;
                            return (
                                <div
                                    className="block group cursor-pointer relative"
                                    key={item.module.id}
                                    onClick={() => navigate(`/lms/course?module=${item.module.title}`, { state: { moduleId: item.module.id, quizeAttemp: item?.module?.users[0]?.quizzes[0]?.attempts?.length } })}
                                    data-discover="true"
                                    style={{ animationDelay: "0ms" }}
                                >
                                    <div className="bg-white/80 backdrop-blur-xl border border-gray-200/50 rounded-3xl overflow-hidden hover:border-purple-300/50 hover:shadow-xl hover:shadow-purple-200/20 transition-all duration-300 group-hover:transform group-hover:scale-[1.02] h-full">
                                        <div className='absolute mt-8' style={{ marginLeft: '-15px' }}>
                                            {item?.module?.users[0]?.moduleProgress[0]?.status === 'completed' && (
                                                <span style={{ borderTopRightRadius: '20px', borderBottomRightRadius: '20px' }} className="px-3 py-1 text-md font-semibold whitespace-nowrap ml-3 bg-white text-green-700 border border-green-200">
                                                    {t('Completed')}
                                                </span>
                                            )}
                                            {item?.module?.users[0]?.moduleProgress[0]?.status === 'in_progress' && (
                                                <span style={{ borderTopRightRadius: '20px', borderBottomRightRadius: '20px' }} className="px-3 py-1 text-md font-semibold whitespace-nowrap ml-3 bg-white text-blue-700 border border-blue-200">
                                                    {t('In Progress')}
                                                </span>
                                            )}
                                            {item?.module?.users[0]?.moduleProgress[0]?.status === 'not_started' && (
                                                <span style={{ borderTopRightRadius: '20px', borderBottomRightRadius: '20px' }} className="px-3 py-1 text-md font-semibold whitespace-nowrap ml-3 bg-white text-gray-700 border border-gray-200">
                                                    {t('Not Started')}
                                                </span>
                                            )}
                                        </div>
                                        <div>
                                            <div className={`flex items-start gap-4 mb-4 ${rtlFlexTextPosition}`}>
                                                <div className={`w-full h-[150px] ${showBgColor(item?.module?.users[0]?.moduleProgress[0]?.status)} shadow-lg group-hover:shadow-purple-300/50 transition-shadow duration-300`}>
                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 673.76 360.09" className="opacity-20 -z-10">
                                                        <defs>
                                                            <style dangerouslySetInnerHTML={{ __html: ".cls-1{fill:#fff;}" }} />
                                                        </defs>
                                                        <g id="Layer_2" data-name="Layer 2">
                                                            <g id="Layer_1-2" data-name="Layer 1">
                                                                <path
                                                                    fill="#F5F5F5"
                                                                    d="M203.41,117l-4.86,24.28L171.87,274.67a31.54,31.54,0,0,1-63.08,0L82.1,141.24,77.25,117a63.08,63.08,0,1,1,126.16,0Z"
                                                                />
                                                                <path
                                                                    fill="#F5F5F5"
                                                                    d="M329.57,243.13a63.08,63.08,0,1,1-126.16,0l4.85-24.28L235,85.42a31.54,31.54,0,1,1,63.08,0l26.69,133.43Z"
                                                                />
                                                                <path
                                                                    fill="#F5F5F5"
                                                                    d="M461.05,63.08l-.75,9.84L429.51,328.55a31.54,31.54,0,1,1-63.08,0L335.67,72.92l-.75-9.84a63.07,63.07,0,1,1,126.13,0Z"
                                                                />
                                                                <path
                                                                    fill="#F5F5F5"
                                                                    d="M587.21,243.13a63.08,63.08,0,0,1-126.16,0l4.85-24.28L492.59,85.42a31.54,31.54,0,1,1,63.08,0l26.69,133.43Z"
                                                                />
                                                                <circle fill="#F5F5F5" cx="31.54" cy="180.05" r="31.54" />
                                                                <circle fill="#F5F5F5" cx="642.22" cy="180.05" r="31.54" />
                                                            </g>
                                                        </g>
                                                    </svg>
                                                </div>
                                            </div>
                                            <div className="px-6 pb-6">
                                                <h3 className="font-medium text-gray-900 text-xl mb-2 group-hover:text-purple-700 transition-colors line-clamp-2">
                                                    {item?.module?.title}
                                                </h3>
                                                <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                                                    {item?.module?.description}
                                                </p>
                                                <div className="space-y-3 mb-4 flex items-center justify-between">
                                                    <div className="flex items-center gap-10 text-sm text-gray-500">
                                                        <div className={`flex items-center ${rtlFlexTextPosition} text-sm text-gray-500`}>
                                                            <span className="flex items-center gap-2">
                                                                <i className="ri-file-text-line text-purple-500" />{item?.module?.is_show_document === true ? item?.module?.documents.length : 0} - {t('Documents')}
                                                            </span>
                                                        </div>
                                                        <span className="flex items-center gap-2">
                                                            <i className="ri-file-list-line text-purple-500" />
                                                            {item.module.quizzes.length > 1 ? `${item?.module?.quizzes?.length} - Quizzes` : `${item.module.quizzes.length} - Quiz`}
                                                        </span>
                                                    </div>
                                                    <span className="flex items-center gap-2 text-[#767676]">
                                                        <i className="ri-time-line" />
                                                        {new Date(createdAt).toLocaleDateString()}
                                                    </span>
                                                </div>
                                                <div>
                                                    <ProgressBar progress={item?.module?.users[0]?.progress_bar || 0} />
                                                    {/* <div className='flex items-center gap-2'>
                                                        <div className="w-full bg-gray-200/50 rounded-full h-2 overflow-hidden">
                                                            <div
                                                                className="bg-gradient-to-r from-[#A73884] to-[#803A85] h-2 rounded-full transition-all duration-500 shadow-sm"
                                                                style={{ width: `${item?.module?.users[0]?.progress_bar}%` }}
                                                            />
                                                        </div>
                                                        <span className="text-black font-medium">{item?.module?.users[0]?.progress_bar}%</span>
                                                    </div> */}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="px-6 py-2 bg-gradient-to-r from-gray-50 to-white border-t border-gray-200/50">
                                            <div className={`flex items-center ${rtlFlexTextPosition}`}>
                                                <span className="text-[#767676] font-medium text-sm group-hover:translate-x-1 transition-transform duration-300">
                                                    {dir === "rtl" ? (
                                                        <>
                                                            <i className="ri-arrow-left-line ml-1" />
                                                            {t('View Course')}
                                                        </>
                                                    ) : (
                                                        <>
                                                            {t('View Course')}
                                                            <i className="ri-arrow-right-line ml-1" />
                                                        </>
                                                    )}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </>
                </div>
            )}
        </main>
    )
}

export default AllCourses;